import { Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLocation } from "@/providers/LocationProvider";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PageHeader } from "@/components/navigation/PageHeader";

interface Package {
  id: string;
  name: string;
  description: string;
  duration_minutes: number;
  base_price_cents: number;
  package_type?: 'VIDEO_ONLY' | 'PHOTO_AND_VIDEO' | 'PHOTO_ONLY' | null;
}

type PackageTypeFilter = 'ALL' | 'VIDEO_ONLY' | 'PHOTO_AND_VIDEO' | 'PHOTO_ONLY';

export default function SelectPackage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<PackageTypeFilter>('ALL');

  // Load previously selected package from localStorage if available
  useEffect(() => {
    try {
      const savedData = localStorage.getItem('bookingFormData');
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.selectedPackageId) {
          setSelectedPackageId(parsed.selectedPackageId);
        }
      }
    } catch (error) {
      console.error('Error loading selected package:', error);
    }
  }, []);

  useEffect(() => {
    // Fetch packages from Supabase
    fetchPackages();
  }, [location]);

  const fetchPackages = async () => {
    try {
      const DEFAULT_LOCATION = 'toronto';
      
      // First get the location ID
      const { data: locationData, error: locationError } = await supabase
        .from("locations")
        .select("id")
        .eq("code", location)
        .eq("is_active", true)
        .maybeSingle();

      if (locationError) throw locationError;
      
      // Se a location não for encontrada, tenta fallback para default
      let finalLocationData = locationData;
      let finalLocationCode = location;
      
      if (!locationData && location !== DEFAULT_LOCATION) {
        console.warn(`Location '${location}' not found, falling back to '${DEFAULT_LOCATION}'`);
        
        const { data: fallbackData, error: fallbackError } = await supabase
          .from("locations")
          .select("id")
          .eq("code", DEFAULT_LOCATION)
          .eq("is_active", true)
          .maybeSingle();
        
        if (fallbackError) {
          console.error("Error fetching fallback location:", fallbackError);
          throw fallbackError;
        }
        
        if (fallbackData) {
          finalLocationData = fallbackData;
          finalLocationCode = DEFAULT_LOCATION;
        }
      }
      
      if (!finalLocationData) {
        toast.error("Location not found");
        return;
      }

      // Then fetch packages for this location
      const { data, error } = await supabase
        .from("packages")
        .select("*")
        .eq("location_id", finalLocationData.id)
        .eq("is_active", true)
        .order("base_price_cents", { ascending: true });

      if (error) throw error;
      
      if (finalLocationCode !== location) {
        console.log(`Using fallback location '${finalLocationCode}' instead of '${location}'`);
      }
      
      setPackages(data || []);
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${remainingMinutes}-min shoot`;
    } else if (remainingMinutes === 0) {
      return `${hours}-hour${hours > 1 ? 's' : ''} shoot`;
    } else {
      return `${hours}h${remainingMinutes}min shoot`;
    }
  };

  // Filter packages based on selected type
  const filteredPackages = packages.filter((pkg) => {
    if (filterType === 'ALL') return true;
    return pkg.package_type === filterType;
  });

  // Clear selection if selected package is not in filtered list
  useEffect(() => {
    if (selectedPackageId && filteredPackages.length > 0) {
      const isSelectedPackageVisible = filteredPackages.some(pkg => pkg.id === selectedPackageId);
      if (!isSelectedPackageVisible) {
        setSelectedPackageId(null);
      }
    }
  }, [filterType, filteredPackages, selectedPackageId]);

  const handleSelectPackage = () => {
    if (!selectedPackageId) {
      toast.error("Please select a package");
      return;
    }

    // Save selected package to localStorage
    try {
      const savedData = localStorage.getItem('bookingFormData');
      const parsed = savedData ? JSON.parse(savedData) : {};
      
      const dataToSave = {
        ...parsed,
        selectedPackageId,
      };
      
      localStorage.setItem('bookingFormData', JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Error saving package selection:', error);
    }

    // Navigate back to step-1
    navigate("/book/step-1");
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-32">
      <PageHeader variant="back" title="Select Package" backTo="/book/step-1" />

      <div className="px-4 py-6 space-y-6">
        {/* Package Type Filter Switch */}
        <div className="bg-white rounded-xl p-1 shadow-sm border border-border">
          <div className="grid grid-cols-4 gap-1">
            <button
              onClick={() => setFilterType('ALL')}
              className={`px-2 sm:px-3 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                filterType === 'ALL'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterType('VIDEO_ONLY')}
              className={`px-2 sm:px-3 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                filterType === 'VIDEO_ONLY'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              Video Only
            </button>
            <button
              onClick={() => setFilterType('PHOTO_AND_VIDEO')}
              className={`px-2 sm:px-3 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                filterType === 'PHOTO_AND_VIDEO'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              Photo & Video
            </button>
            <button
              onClick={() => setFilterType('PHOTO_ONLY')}
              className={`px-2 sm:px-3 py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 ${
                filterType === 'PHOTO_ONLY'
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              Photo Only
            </button>
          </div>
        </div>

        <div>
          <h2 className="text-lg font-semibold mb-4">
            {filterType === 'ALL' 
              ? 'All Available Packages' 
              : filterType === 'VIDEO_ONLY'
              ? 'Video Only Packages'
              : filterType === 'PHOTO_AND_VIDEO'
              ? 'Photo & Video Packages'
              : 'Photo Only Packages'}
            {filteredPackages.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({filteredPackages.length})
              </span>
            )}
          </h2>

          <div className="space-y-3">
            {loading ? (
              <>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Card key={i} className="p-4 bg-[#F4F5F7] relative overflow-hidden">
                    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/40 to-transparent" />
                    <div className="flex justify-between items-start mb-2">
                      <div className="h-6 w-32 bg-muted/60 rounded animate-pulse"></div>
                      <div className="h-6 w-16 bg-muted/60 rounded animate-pulse"></div>
                    </div>
                    <div className="h-4 w-full bg-muted/60 rounded mb-2 animate-pulse"></div>
                    <div className="h-4 w-3/4 bg-muted/60 rounded mb-3 animate-pulse"></div>
                    <div className="h-4 w-24 bg-muted/60 rounded animate-pulse"></div>
                  </Card>
                ))}
              </>
            ) : filteredPackages.length === 0 ? (
              <Card className="p-4 bg-[#F4F5F7]">
                <p className="text-muted-foreground text-center">
                  {packages.length === 0 
                    ? 'No packages available' 
                    : `No ${filterType === 'VIDEO_ONLY' ? 'Video Only' : filterType === 'PHOTO_AND_VIDEO' ? 'Photo & Video' : 'Photo Only'} packages available`}
                </p>
              </Card>
            ) : (
              filteredPackages.map((pkg, index) => (
                <Card
                  key={pkg.id}
                  className={`p-4 cursor-pointer transition-all bg-[#F4F5F7] animate-fade-in ${
                    selectedPackageId === pkg.id
                      ? "border-primary border-2 shadow-sm"
                      : "border-border hover:border-primary/50"
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                  onClick={() => setSelectedPackageId(pkg.id)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-foreground">{pkg.name}</h3>
                    <span className="font-bold text-lg">{formatPrice(pkg.base_price_cents)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{pkg.description}</p>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>{formatDuration(pkg.duration_minutes)}</span>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <div
                      className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${
                        selectedPackageId === pkg.id ? "border-primary bg-primary" : "border-input"
                      }`}
                    >
                      {selectedPackageId === pkg.id && <div className="h-3 w-3 rounded-full bg-primary-foreground"></div>}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4">
        <Button
          onClick={handleSelectPackage}
          className="w-full h-14 text-base font-semibold rounded-xl"
          disabled={!selectedPackageId}
        >
          Select Package
        </Button>
      </div>
    </div>
  );
}

